import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../redux/store';
import Modal from '../../molecules/Modal/Modal';
import ArrowCircleLeft from '../../../assets/img/svg/arrow_circle_left.svg';
import Congratulations from '../../../assets/img/svg/congratulations.svg';
import Text from '../../atoms/Text';
import React from 'react';
import { getBrandIcon } from '../../../shared/utils/payments.util';
import { formatPrice } from '../../../shared/utils/general.util';
import Button from '../../atoms/Button';
import {
	prebillingPreviewSubscription,
	prebillingSubscription,
} from '../../../services/suscription.service';
import { SubscriptionPrebillingPreview } from '../../../@types/SubscriptionPrebilling.type';
import { ALERT_TYPE } from '../../molecules/Alert';
import { AlertPayload, showAlert } from '../../../redux/slices/alert.slice';

interface Props {
	showModal: boolean;
	setShowModal: Dispatch<SetStateAction<any>>;
	onClose?: (isPrebillingCompleted: boolean) => void;
	subscriptionId: number | null;
}

export type AssignOrgType = {
	org_code: string;
};

const ModalPrebilling: React.FC<Props> = ({
	showModal,
	setShowModal,
	subscriptionId,
	onClose,
}) => {
	const dispatch = useDispatch();
	const [isLoadingButton, setIsLoadingButton] = useState(false);
	const [isDisabledButton, setIsDisabledButton] = useState(true);
	const [isPrebillingCompleted, setIsPrebillingCompleted] = useState(false);
	const [prebillingPreview, setPrebillingPreview] =
		useState<SubscriptionPrebillingPreview | null>(null);
	const { tokenSession } = useSelector((state: RootState) => state.auth);

	const handleCloseModal = () => {
		setIsPrebillingCompleted(false);
		setShowModal(false);
		setPrebillingPreview(null);
		setIsDisabledButton(true);
		setIsLoadingButton(false);
		onClose?.(isPrebillingCompleted);
	};
	const showAlertTop = ({ title, type, description }: AlertPayload) => {
		const alert = {
			type,
			title,
			description,
		};
		dispatch(showAlert(alert));
	};

	const executePrebilling = async () => {
		try {
			setIsLoadingButton(true);
			setIsDisabledButton(true);
			await prebillingSubscription(subscriptionId as number, tokenSession);
			setIsPrebillingCompleted(true);
		} catch (error) {
			const errorResponse = error as JsonObject;
			const message = errorResponse?.response?.data?.resource?.message;
			showAlertTop({
				title: 'Error',
				type: ALERT_TYPE.ERROR,
				description: message || errorResponse?.message,
			});
			setIsPrebillingCompleted(false);
			setIsDisabledButton(false);
		} finally {
			setIsLoadingButton(false);
		}
	};

	const executePrebillingPreview = async () => {
		try {
			setIsDisabledButton(true);
			const response = await prebillingPreviewSubscription(
				subscriptionId as number,
				tokenSession,
			);
			setPrebillingPreview(response);
			setIsDisabledButton(false);
		} catch (error) {
			const errorResponse = error as JsonObject;
			const message = errorResponse?.response?.data?.resource?.message;
			showAlertTop({
				title: 'Error',
				type: ALERT_TYPE.ERROR,
				description: message || errorResponse?.message,
			});
		}
	};

	useEffect(() => {
		if (showModal && subscriptionId) {
			executePrebillingPreview();
		}
	}, [showModal, subscriptionId]);

	return (
		<Modal
			open={showModal}
			onClose={handleCloseModal}
			sx='w-[70%] !max-w-[440px]'
			showCloseButton={true}
		>
			{!isPrebillingCompleted ? (
				<div className='p-4'>
					<div className='flex justify-center items-center gap-2 mb-4 '>
						<img
							src={ArrowCircleLeft}
							alt='arrow-circle'
							className='w-10 h-10 min-w-10 min-h-10'
						/>
					</div>
					<div className=' flex justify-center items-center'>
						<p className='text-base text-center mt-2'>
							<b>
								Anticipa la renovación de tu plan de Declaración Anual
							</b>
						</p>
					</div>
					<br />
					<div className='flex items-center justify-center w-full'>
						<div className='mb-6 text-s  w-11/12 font-light'>
							<p className='text-gray-600 mb-4'>
								Ventajas de adelantar tu renovación:
							</p>
							<ul className='list-disc pl-6 text-gray-600'>
								<li>Tu renovación tendrá un descuento especial por pago adelantado.</li>
								<li>
									Tendremos el tiempo suficiente para revisar y preparar tu declaración anual para su presentación en Abril.
								</li>
								<li>
									Tendrás prioridad en la atención durante Abril.
								</li>
							</ul>
						</div>
					</div>
					<hr />

					<div className='flex justify-between items-center gap-2 font-ligth mt-4'>
						<Text
							size='m'
							weight='light'
						>
							Información de Pago:
						</Text>
						<div className='flex items-center gap-2 text-m font-light'>
							<img
								src={getBrandIcon(prebillingPreview?.token.brand || '')}
								alt='brand'
								className='w-[24px] h-[24px] ml-2'
							/>
							{prebillingPreview?.token.brand} ****
							{prebillingPreview?.token.last4}
						</div>
					</div>

					<div className='flex justify-between items-center gap-2 font-ligth mt-4'>
						<Text
							size='m'
							weight='bold'
						>
							Total:
						</Text>

						<Text
							size='m'
							weight='bold'
						>
							${formatPrice(prebillingPreview?.shopping_cart.total)}
						</Text>
					</div>
					<br />
					<Button
						variant='green'
						label='Adelantar mi renovación'
						sx='!w-[90%] mx-auto'
						onClick={() => executePrebilling()}
						loading={isLoadingButton}
						disabled={isDisabledButton}
					/>
				</div>
			) : (
				<div className='p-2'>
					<div className='flex justify-center items-center gap-2'>
						<img
							src={Congratulations}
							alt='congratulations'
							className='w-12 h-12 min-w-12 min-h-12'
						/>
					</div>
					<div className=' flex justify-center items-center'>
						<p className='text-base/5 w-10/12 mt-2 ml-8 break-words'>
							Tu renovación para la{' '}
							<b className=' font-bold'>Declaración anual 2024</b> está lista y
							asegurada.
						</p>
					</div>
					<br />
					<div className='flex items-center justify-center w-full'>
						<div className='text-xs  w-11/12 font-light'>
							<p className='text-gray-600 text-left'>
								Te mantendremos al tanto de todo el progreso y de cualquier tipo
								de documentación, información o acción que necesitemos de tu
								lado.
							</p>
						</div>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default ModalPrebilling;
